import { useEffect } from 'react';
import { api } from '@ui/store/baseApi';
import { useAppDispatch } from '@ui/store';

/**
 * Server-Sent Events from the API say "calls changed" / "campaigns
 * changed"; we invalidate those tags and RTK Query refetches what is on
 * screen. The stream carries no records, so tenancy stays server-side.
 *
 * EventSource cannot refresh an expired session by itself (the access
 * cookie lives 15 minutes), so a dropped stream first renews the session,
 * then reconnects, backing off from 5 s to a minute. A tab left open
 * overnight therefore costs one request a minute, not one every 5 seconds.
 */
const MIN_RETRY_MS = 5_000;
const MAX_RETRY_MS = 60_000;

export function useLiveEvents(path: string | null): void {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!path) return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let delay = MIN_RETRY_MS;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      source = new EventSource(path, { withCredentials: true });
      source.onopen = () => {
        delay = MIN_RETRY_MS;
      };
      source.addEventListener('change', (event) => {
        try {
          const { kinds } = JSON.parse((event as MessageEvent).data) as { kinds: string[] };
          const tags: ('Call' | 'Campaign' | 'Message' | 'Dashboard' | 'Billing' | 'AdminOrg')[] = ['Dashboard'];
          if (kinds.includes('call')) tags.push('Call', 'Billing', 'AdminOrg');
          if (kinds.includes('campaign')) tags.push('Campaign');
          if (kinds.includes('message')) tags.push('Message');
          dispatch(api.util.invalidateTags(tags));
        } catch {
          // A malformed event is ignored; the next one refreshes anyway.
        }
      });
      source.onerror = () => {
        source?.close();
        if (stopped) return;
        retry = setTimeout(() => {
          // Renew the session first (a no-op failure if it cannot be renewed), then reconnect.
          void fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
            .catch(() => undefined)
            .finally(connect);
        }, delay);
        delay = Math.min(delay * 2, MAX_RETRY_MS);
      };
    };

    connect();
    return () => {
      stopped = true;
      source?.close();
      if (retry) clearTimeout(retry);
    };
  }, [path, dispatch]);
}
